import { Navigate } from 'react-router-dom';
import { LoadingSpinner } from '@gradebook/ui';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { useSchoolSettings } from '@/hooks/useSchoolSettings';
import { usePrefetchCourseScope } from '@/hooks/useGradebookQueries';
import { useAuthStore } from '@/store/authStore';

interface GradebookRouteProps {
  children: React.ReactNode;
  /** Passed straight through to `ProtectedRoute` — the module never widens access. */
  roles?: Array<'admin' | 'teacher' | 'student' | 'parent'>;
  /**
   * Whether to warm the course scope while the guard waits. On for the pages
   * that read it; off for the calendar, which is behind the guard because it
   * lists assignment due dates, not because it needs the catalogue.
   */
  prefetchCourseScope?: boolean;
}

/**
 * A route that exists only while the school runs the gradebook module.
 *
 * `ProtectedRoute` with one more question asked after the role one: does this
 * school offer grades at all? A school can be live on attendance months before
 * its grade scales, weighted areas and posting windows are settled, and hiding
 * the nav links is not enough on its own — a bookmark, the browser's history
 * and an old email all still point straight at `/:slug/gradebook`.
 *
 * Written as a wrapper *around* `ProtectedRoute` rather than a flag on it so
 * that the ~30 routes with nothing to do with grades keep a guard that reads
 * only from the auth store and issues no request.
 */
export function GradebookRoute({
  children,
  roles,
  prefetchCourseScope = true,
}: GradebookRouteProps) {
  return (
    <ProtectedRoute roles={roles}>
      <GradebookModuleGuard prefetchCourseScope={prefetchCourseScope}>
        {children}
      </GradebookModuleGuard>
    </ProtectedRoute>
  );
}

/**
 * Rendered only once `ProtectedRoute` has admitted the visitor, so `user` is
 * present by construction.
 *
 * Waits for the setting rather than assuming it. Everywhere else in the app an
 * absent `gradebookEnabled` means "on" — the answer that keeps a working
 * gradebook working while the request is in flight. Here that default would
 * render a grade for a school that has said no, for as long as the request
 * takes, and every school now starts out saying no. So the page holds until the
 * answer arrives; an outright failure still falls back to "on", because a
 * network hiccup must not take a working gradebook away.
 */
function GradebookModuleGuard({
  children,
  prefetchCourseScope,
}: {
  children: React.ReactNode;
  prefetchCourseScope: boolean;
}) {
  const user = useAuthStore((state) => state.user);
  const { data: school, isLoading } = useSchoolSettings();

  // Started here rather than in the page: which courses exist and which sections
  // run in each term is not an answer this guard is waiting on, and every
  // gradebook read hangs off the term-course id resolved from those two. Left to
  // the page they queued behind this guard, which put a whole round trip between
  // arriving and the first request the gradebook actually needs.
  usePrefetchCourseScope(prefetchCourseScope);

  if (isLoading) {
    return <LoadingSpinner text="Loading…" />;
  }

  if (!(school?.gradebookEnabled ?? true)) {
    // An empty slug would make this `//dashboard`, which a browser reads as a
    // protocol-relative URL to a host called "dashboard" - so fall back to the
    // root redirect instead of building a path out of nothing.
    return <Navigate to={user?.schoolSlug ? `/${user.schoolSlug}/dashboard` : '/'} replace />;
  }

  return <>{children}</>;
}
